import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // Remove authorization header if no token
      delete config.headers.Authorization;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Company API
export const companyAPI = {
  get: () => api.get('/company'),
  update: (data) => api.put('/company', data),
  updateStats: (stats) => api.put('/company/stats', { stats }),
};

// Courses API
export const coursesAPI = {
  getAll: (params = {}) => api.get('/courses', { params }),
  getBySlug: (slug) => api.get(`/courses/${slug}`),
  getById: (id) => api.get(`/courses/${id}`), // Keep for backward compatibility
  create: (data) => api.post('/courses', data),
  update: (slug, data) => api.put(`/courses/${slug}`, data),
  delete: (slug) => api.delete(`/courses/${slug}`),
  getCategories: () => api.get('/courses/categories/list'),
  getFeatured: () => api.get('/courses/featured/list'),
  enroll: (courseId) => api.post(`/courses/${courseId}/enroll`),
};

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  getEnrollments: () => api.get('/auth/enrollments'),
};

// Contact API
export const contactAPI = {
  submit: (data) => api.post('/contact', data),
  getAll: (params = {}) => api.get('/contact', { params }),
  getById: (id) => api.get(`/contact/${id}`),
  updateStatus: (id, data) => api.put(`/contact/${id}/status`, data),
  delete: (id) => api.delete(`/contact/${id}`),
};

// Admin API
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard', {headers: {Authorization: localStorage.getItem('token') || ""}}),
  getUsers: (params = {}) => api.get('/admin/users', { params }),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  getCourses: (params = {}) => api.get('/admin/courses', { params }),
  getEnrollments: (params = {}) => api.get('/admin/enrollments', { params }),
  getContacts: (params = {}) => api.get('/admin/contacts', { params }),
};

export const mediaAPI = {
  uploadImage: (formData) => api.post('/media/upload/image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadImages: (formData) => api.post('/media/upload/images', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadCourseImage: (formData) => api.post('/media/upload/course-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadProfileImage: (formData) => api.post('/media/upload/profile-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadCompanyLogo: (formData) => api.post('/media/upload/company-logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadFiles: (formData) => api.post('/media/upload/files', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteFile: (filename, type = 'general') => api.delete(`/media/delete/${filename}?type=${type}`)
};

export const registrationAPI = {
  getMyRegistrations: () => api.get('/registrations/my-registrations'),
  register: (data) => api.post('/registrations/register', data),
  cancel: (id) => api.put(`/registrations/${id}/cancel`),
  getAll: (params = {}) => api.get('/registrations/admin/all', { params }),
  updateStatus: (id, status) => api.put(`/registrations/admin/${id}/status`, { status }),
  delete: (id) => api.delete(`/registrations/${id}`)
};

// Team Members API
export const teamMembersAPI = {
  getAll: (params) => api.get('/team-members',{params}),
  getById: (id) => api.get(`/team-members/${id}`),
  create: (data) => api.post('/team-members', data),
  update: (id, data) => api.put(`/team-members/${id}`, data),
  delete: (id) => api.delete(`/team-members/${id}`),
};

export default api;

